import Minze, { EventListeners, MinzeElement, Reactive } from "minze";
import { Locale } from "../../modules/locale";
import { EMAIL_REGEX } from "../../modules/utils";
import { submitForm } from "../../constants";
import { SquidexTextLabels } from "../../modules/squidex/labels";

Locale.getInstance().registerKeySet("fr", "trainingForm", {
  nameLabel: "Prénom",
  surnameLabel: "Nom",
  emailLabel: "Adresse courriel",
  schoolLabel: "Nom de l’école ou du centre de service scolaire",
  regionLabel: "Région",
  schoolParticipantsLabel:
    "Nombre d’écoles participantes (si centre de services scolaires) (optionnel)",
  participantsLabel: "Nombre de participant.e.s attendu (optionnel)",
  roleLabel: "Fonction des personnes participantes",
  datesLabel: "Date(s) souhaitée(s)",
  hourLabel: "Horaire",
  formatLabel: "Format",
  messageHeading: "Informations supplémentaires sur votre demande",
  messageLabel: "Écrivez votre message...",
  send: "Envoyer",
  requiredError: "Requis",
  invalidEmailError: "Le courriel est invalide",
  invalidPhoneError: "Le numéro de téléphone est invalide",
  submittedMessage: "Votre demande a été envoyée.",
  regionsOptionLabels:
    "Abitibi-Témiscamingue,Bas-Saint-Laurent,Capitale-Nationale,Centre-du-Québec,Chaudière-Appalaches,Côte-Nord,Estrie,Gaspésie–Îles-de-la-Madeleine,Lanaudière,Laurentides,Laval,Mauricie,Montérégie,Montréal,Nord-du-Québec,Outaouais,Saguenay–Lac-Saint-Jean",
  rolesOptionLabels:
    "Enseignant.e.s,Enseignant.e.s CCQ,Intervenant.e.s scolaires,Équipe d’administration et membres de la direction,Conseiller.ère.s pédagogiques,Autre",
  hoursOptionLabels: "AM (9h-12h),PM (13h-16h),Autre",
  formatsOptionLabels:
    "Présentiel (gratuit/incluant les frais de déplacement),Virtuel (gratuit)",
});

Locale.getInstance().registerKeySet("en", "trainingForm", {
  nameLabel: "First Name",
  surnameLabel: "Last Name",
  emailLabel: "Email",
  schoolLabel: "Name of School or Academic Services Centre",
  regionLabel: "Region",
  schoolParticipantsLabel:
    "Number of participating schools (if Academic Services Centre) (optional)",
  participantsLabel: "Number of participants  (optional)",
  roleLabel: "Role of participants",
  datesLabel: "Date(s) Requested",
  hourLabel: "Time slot",
  formatLabel: "Format",
  messageHeading: "Additional information regarding your request",
  messageLabel: "Write your message:",
  send: "Submit",
  requiredError: "Required",
  invalidEmailError: "Email is invalid",
  invalidPhoneError: "Phone number is invalid",
  submittedMessage: "Your request has been sent.",
  regionsOptionLabels:
    "Abitibi-Témiscamingue,Bas-Saint-Laurent,Capitale-Nationale,Centre-du-Québec,Chaudière-Appalaches,Côte-Nord,Estrie,Gaspésie–Îles-de-la-Madeleine,Lanaudière,Laurentides,Laval,Mauricie,Montérégie,Montréal,Nord-du-Québec,Outaouais,Saguenay–Lac-Saint-Jean",
  rolesOptionLabels:
    "Teacher(s),CCQ Teacher(s),School Counsellor(s),Member of the Administration,Academic Advisor,Other",
  hoursOptionLabels: "AM (9:00 AM to 12:00 PM),PM (1:00 PM to 4:00 PM),Other",
  formatsOptionLabels:
    "In-person (free; includes transportation costs), Virtual (free)",
});

const trainingOptions = [
  "abitibi-témiscamingue",
  "bas-saint-laurent",
  "capitale-nationale",
  "centre-du-québec",
  "chaudière-appalaches",
  "côte-nord",
  "estrie",
  "gaspésie–îles-de-la-madeleine",
  "lanaudière",
  "laurentides",
  "laval",
  "mauricie",
  "montérégie",
  "montréal",
  "nord-du-québec",
  "outaouais",
  "saguenay–lac-saint-jean",
];
const hoursOptions = ["am(9-12)", "pm(13-16)", "autre"];
const formatsOptions = ["face-to-face", "virtual"];

export interface TjTrainingForm {
  errors: { [fieldName: string]: string };
  values: { [fieldName: string]: any };
  submitted: boolean;
}

export class TjTrainingForm extends MinzeElement {
  reactive: Reactive = [
    ["errors", {}],
    ["values", {}],
    ["submitted", false],
  ];
  isMobile = window.matchMedia("(max-width: 768px)").matches;

  submitForm = async () => {
    this.submitted = false;
    this.errors = {};
    const nameField = this.select(`#name`)?.getAttribute("value");
    const emailField = this.select(`#email`)?.getAttribute("value");
    const schoolField = this.select(`#school`)?.getAttribute("value");
    const serviceCenter = this.select(`#serviceCenter`)?.getAttribute("value");
    const regionField = this.select(`#region`)?.getAttribute("value");
    const participantsField =
      this.select(`#participants`)?.getAttribute("value");
    const roleField = this.select(`#role`)?.getAttribute("value");
    const datesField = this.select(`#dates`)?.getAttribute("value");
    const hourField = this.select(`#hour`)?.getAttribute("value");
    const formatField = this.select(`#format`)?.getAttribute("value");
    const messageField = this.select(`#message`)?.getAttribute("value");
    this.values = {
      name: nameField,
      email: emailField,
      school: schoolField,
      serviceCenter: serviceCenter,
      region: regionField,
      participants: participantsField,
      role: roleField,
      dates: datesField,
      hour: hourField,
      format: formatField,
      message: messageField,
    };
    if (!nameField)
      this.errors = {
        ...this.errors,
        name: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!emailField)
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    else if (!EMAIL_REGEX.test(emailField)) {
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("trainingForm.invalidEmailError"),
      };
    }
    if (!schoolField)
      this.errors = {
        ...this.errors,
        school: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
      if (!serviceCenter)
        this.errors = {
          ...this.errors,
          serviceCenter: SquidexTextLabels.getKey("trainingForm.requiredError"),
        };
    if (!regionField)
      this.errors = {
        ...this.errors,
        region: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!participantsField)
      this.errors = {
        ...this.errors,
        participants: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!datesField)
      this.errors = {
        ...this.errors,
        dates: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!hourField)
      this.errors = {
        ...this.errors,
        hour: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!formatField)
      this.errors = {
        ...this.errors,
        format: SquidexTextLabels.getKey("trainingForm.requiredError"),
      };
    if (!Object.values(this.errors).length) {
      const response = await submitForm("354419g", this.values);
      if (response.status === "success") {
        this.submitted = true;
        this.values = {};
        Minze.cast("tj:training-form-submitted");
        this.rerender(true);
      }
    }
    this.rerender();
  };
  html = () => `
    <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
      <tj-input
        id="name"
        name="name"
        placeholder="${SquidexTextLabels.getKey("trainingForm.nameLabel")}"
        error="${this.errors["name"]}"
        value="${this.values["name"]}"
      ></tj-input>
      <tj-input
        id="email"
        name="email"
        type="email"
        placeholder="${SquidexTextLabels.getKey("trainingForm.emailLabel")}"
        error="${this.errors["email"]}"
        value="${this.values["email"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
      <tj-input
        id="school"
        name="school"
        placeholder="${SquidexTextLabels.getKey("trainingForm.schoolLabel")}"
        error="${this.errors["school"]}"
        value="${this.values["school"]}"
      ></tj-input>
      <tj-input
        id="serviceCenter"
        name="serviceCenter"
        placeholder="${SquidexTextLabels.getKey("trainingForm.serviceCenter")}"
        error="${this.errors["serviceCenter"]}"
        value="${this.values["serviceCenter"]}"
      ></tj-input>
      
    </tj-box>
    <tj-box mb="16px">
    <tj-select
        id="region"
        name="region"
        placeholder="${SquidexTextLabels.getKey("trainingForm.regionLabel")}"
        default-value=""
        selectOptions="${trainingOptions.join(",")}"
        selectOptionLabels="${SquidexTextLabels.getKey("trainingForm.regionsOptionLabels")}"
        error="${this.errors["region"]}"
        value="${this.values["region"]}"
      ></tj-select>
    </tj-box>
    <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
      <tj-input
        type="number"
        id="participants"
        name="participants"
        placeholder="${SquidexTextLabels.getKey("trainingForm.participantsLabel")}"
        error="${this.errors["participants"]}"
        value="${this.values["participants"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
      <tj-input
        id="dates"
        name="dates"
        placeholder="${SquidexTextLabels.getKey("trainingForm.datesLabel")}"
        error="${this.errors["dates"]}"
        value="${this.values["dates"]}"
      ></tj-input>
      <tj-select
        id="hour"
        name="hour"
        placeholder="${SquidexTextLabels.getKey("trainingForm.hourLabel")}"
        default-value=""
        selectOptions="${hoursOptions.join(",")}"
        selectOptionLabels="${SquidexTextLabels.getKey("trainingForm.hoursOptionLabels")}"
        error="${this.errors["hour"]}"
        value="${this.values["hour"]}"
      ></tj-select>
    </tj-box>
    <tj-box mb="16px">
      <tj-select
        id="format"
        name="format"
        placeholder="${SquidexTextLabels.getKey("trainingForm.formatLabel")}"
        default-value=""
        selectOptions="${formatsOptions.join(",")}"
        selectOptionLabels="${SquidexTextLabels.getKey("trainingForm.formatsOptionLabels")}"
        error="${this.errors["format"]}"
        value="${this.values["format"]}"
      ></tj-select>
    </tj-box>
    <tj-box mb="8px">
      <tj-text color="#133762" font-size="16px">${SquidexTextLabels.getKey("trainingForm.messageHeading")}</tj-text>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="message"
        name="message"
        textarea="true"
        placeholder="${SquidexTextLabels.getKey("trainingForm.messageLabel")}"
        error="${this.errors["message"]}"
        value="${this.values["message"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px" display="flex" alignItems="center" justifyContent="space-between">
      <tj-button id="submit" name="save" size="small" width="fit-content"
      class="training-form-submit-button"
      >
        ${SquidexTextLabels.getKey("trainingForm.send")}
      </tj-button>
      ${this.submitted ? `<tj-text>${SquidexTextLabels.getKey("trainingForm.submittedMessage")}</tj-text>` : ""}
    </tj-box>
  `;

  css = () => `
    .title {
      font-weight: 500;
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
      margin: 80px 0 8px 0;
    }
  `;
  eventListeners: EventListeners = [["#submit", "click", this.submitForm]];
}
